import React, { useEffect, useState, useRef } from "react";
import bgImg from "../../assets/images/bg-img-1.png";
import AvatarPlaceholder from "../../assets/images/avatar-placeholder.svg";
import { getAirlines, getUserProfile, updateUserProfile, updateProfilePicture, getProfilePicture, getExcludedAirlines } from "apis/settingsServices";
import { useAuth } from "../../contexts/AuthContext";
import { useUserStore } from "store/userStore";
import { useStore } from "store/zustand";
import { useToastStore } from "store/toastStore";
import AsyncSelect from "react-select/async";
import DeleteAccountModal from "./components/DeleteAccountModal";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import { GroupBase, StylesConfig } from "react-select";
import { isIOSDevice } from "utils/device";

// The backend overwrites the same storage key on every upload, so the URL
// getProfilePicture() returns is identical before and after — the browser's
// HTTP cache serves the OLD cached bytes for that exact URL instead of
// refetching, even once the backend genuinely has the new image. A
// cache-busting query param makes every request for "the current photo"
// unique, forcing a real fetch. Needed everywhere this page turns a
// getProfilePicture() response into an <img> src, not just right after
// upload — including the mount effect, which runs again (with a plain,
// non-busted URL) every time this component remounts, e.g. navigating away
// from Settings and back, which is what made the old photo reappear only in
// that case and not on a hard refresh (that revalidates instead of reusing
// the earlier cached response).
const withCacheBust = (url: string): string =>
  url.startsWith("data:") ? url : `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;

// Define proper interfaces for the airline option type
interface AirlineOption {
  value: string | number;
  label: string;
}

interface AirlineData {
  id: number;
  airlineName: string;
}

interface HomebaseData {
  id: number;
  name: string;
}

interface UserProfileData {
  email: string;
  fullName: string;
  homebase: HomebaseData;
  primaryAirline: AirlineData;
  additionalAirlines: AirlineData[];
  // Add other properties as needed
}

const customAsyncStyles: StylesConfig<AirlineOption, false, GroupBase<AirlineOption>> = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#F5F9FE",
    border: "none",
    minHeight: "50px",
    borderRadius: "8px",
    fontSize: "14px",
    paddingLeft: "4px",
    transition: "all 0.2s ease-in-out",
    boxShadow: "none !important",
    outline: "none !important",

    "&:hover": {
      boxShadow: "none",
    },

    // removes react-select default focus ring
    "&.react-select__control--is-focused": {
      boxShadow: "none !important",
      outline: "none !important",
      border: "none",
    }
  }),

  input: (provided) => ({
    ...provided,
    color: "#000",
    boxShadow: "none !important",
    outline: "none !important",
    border: "none !important",

    "& input": {
      boxShadow: "none !important",
      outline: "none !important",
    }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#F5F9FE",
    borderRadius: "8px",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    zIndex: 50,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#DCEEFF" : state.isFocused ? "#EAF4FF" : "#F5F9FE",
    color: "#000",
    cursor: "pointer",
    padding: "10px 12px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#000",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9CA3AF",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    color: "#6B7280",
  }),
};

const Profile: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const { username, email } = useUserStore((state) => state);
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [error, setError] = useState<string>("");
  const { setIsAuthenticated } = useStore();
  const { showToast } = useToastStore();
  const [showAdditionalAirline, setShowAdditionalAirline] = useState(false);
  const [airline1, setAirline1] = useState<AirlineOption | null>(null);
  const [airline2, setAirline2] = useState<AirlineOption | null>(null);
  const [airline3, setAirline3] = useState<AirlineOption | null>(null);
  const [airline4, setAirline4] = useState<AirlineOption | null>(null);
  const [originalAirline1, setOriginalAirline1] = useState<AirlineOption | null>(null);
  const [originalAirline2, setOriginalAirline2] = useState<AirlineOption | null>(null);
  const [originalAirline3, setOriginalAirline3] = useState<AirlineOption | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // Airline ids already on the Excluded Airlines list (Flight Search
  // settings) — an additional airline can't also be excluded, so this
  // guards Save with a clear message instead of a generic "failed to
  // update" from the backend.
  const [excludedAirlineIds, setExcludedAirlineIds] = useState<Set<number>>(new Set());
  const [profilePhoto, setProfilePhoto] = useState<string>(AvatarPlaceholder);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Guards against a race between this mount-time fetch and a manual upload:
  // if the user selects and confirms a new photo before this effect's own
  // getProfilePicture() call resolves, that slower, now-stale response would
  // land afterwards and overwrite the just-uploaded photo with the old one —
  // exactly the "photo reverts a few seconds after the success message"
  // symptom. Set by handlePhotoUpload once a fresh upload has landed.
  const hasUploadedPhotoRef = useRef(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      showLoader();
      try {
        const profileData = await getUserProfile(); // Fetch the user profile

        // Fetch the profile picture (isolated — a failure here must not block
        // the additional-airline initialization below)
        try {
          const profilePictureData = await getProfilePicture();
          if (!hasUploadedPhotoRef.current && profilePictureData?.profilePictureUrl) {
            const bustedUrl = withCacheBust(profilePictureData.profilePictureUrl);
            setProfilePhoto(bustedUrl);
            // Keep the shared store in sync so the header avatar matches.
            useUserStore.getState().setProfilePictureUrl(bustedUrl);
          }
        } catch (pictureErr) {
          console.warn("No profile picture available", pictureErr);
        }

        // Build airline options synchronously BEFORE any state updates so
        // they're ready by the time the Additional Airlines section renders.
        const additionalAirlines = Array.isArray(profileData?.additionalAirlines)
          ? profileData.additionalAirlines
          : [];

        const buildOption = (a: AirlineData | undefined): AirlineOption | null =>
          a ? { value: a.id, label: a.airlineName } : null;

        const opt1 = buildOption(additionalAirlines[0]);
        const opt2 = buildOption(additionalAirlines[1]);
        const opt3 = buildOption(additionalAirlines[2]);

        // Apply airline state FIRST so AsyncSelect's `value` prop is populated
        // by the time the section becomes visible.
        setAirline1(opt1);
        setOriginalAirline1(opt1);
        setAirline2(opt2);
        setOriginalAirline2(opt2);
        setAirline3(opt3);
        setOriginalAirline3(opt3);
        if (additionalAirlines.length > 0) {
          setShowAdditionalAirline(true);
        }

        // Set userProfile LAST: it's what reveals the Additional Airlines
        // section via the visibility condition on line ~486.
        setUserProfile(profileData);
      } catch (err) {
        console.error("Failed to fetch user profile:", err);
      } finally {
        hideLoader();
      }
    };

    fetchUserProfile(); // Call the function to fetch data
  }, [showToast]);

  useEffect(() => {
    getExcludedAirlines()
      .then((response) => {
        if (Array.isArray(response)) {
          setExcludedAirlineIds(
            new Set(response.map((airline: { id: number }) => airline.id)),
          );
        }
      })
      .catch((err) => console.error("Failed to fetch excluded airlines", err));
  }, []);

  // Check for changes whenever the airline selections change
  useEffect(() => {
    // Function to compare two airline options (handles null cases too)
    const areOptionsEqual = (option1: AirlineOption | null, option2: AirlineOption | null) => {
      if (!option1 && !option2) return true;
      if (!option1 || !option2) return false;
      return option1.value === option2.value;
    };

    const hasAirlineChanges =
      !areOptionsEqual(airline1, originalAirline1) ||
      !areOptionsEqual(airline2, originalAirline2) ||
      !areOptionsEqual(airline3, originalAirline3);

    setHasChanges(hasAirlineChanges);
  }, [airline1, airline2, airline3, airline4, originalAirline1, originalAirline2, originalAirline3]);

  const logoutFunction = () => {
    setIsAuthenticated(false);
    // Reset the whole user store, not just localStorage — the in-memory state
    // outlives this client-side navigation, so anything left behind (e.g. the
    // profile picture) would show up for the next user who logs in.
    useUserStore.getState().resetUserDetails();
    localStorage.removeItem("token");
    localStorage.removeItem("userDetails");
  };

  const handlePhotoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        showToast("Please select a valid image file", "error", 3000);
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast("File size must be less than 5MB", "error", 3000);
        return;
      }

      // Read file and show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        setPhotoPreview(imageData);
        setIsPhotoModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoPreview) return;

    try {
      showLoader();
      // Convert base64 to blob and then to File
      const response = await fetch(photoPreview);
      const blob = await response.blob();
      const file = new File([blob], "profile-photo.png", { type: blob.type });

      // Call the updateProfilePicture API
      await updateProfilePicture(file);

      // Show the exact bytes just uploaded immediately, rather than the
      // persisted URL from a re-fetch: the backend can take a moment to
      // finish processing/propagating the new file at that same storage
      // key, so an immediate getProfilePicture() call right after upload can
      // still return the OLD image — even with a cache-busting query param,
      // since that only defeats the browser's cache, not a server/CDN
      // response that's genuinely still serving the previous file. The
      // local preview has no such dependency: it's the literal file the
      // user just picked, so it's correct without waiting on the backend.
      hasUploadedPhotoRef.current = true;
      setProfilePhoto(photoPreview);
      // Same reasoning for the shared store — the header avatar reads this
      // directly, so it needs the immediately-correct bytes too, not a
      // persisted URL that might still resolve to the old file for a bit.
      useUserStore.getState().setProfilePictureUrl(photoPreview);
      setIsPhotoModalOpen(false);
      setPhotoPreview(null);
      showToast("Profile photo updated successfully", "success", 3000);

      // Sync the shared store with the real persisted URL in the background
      // (so the header avatar and other pages don't keep a giant base64
      // string forever) — this only updates the store, never this page's
      // own `profilePhoto`, so a slow/stale response here can't make the
      // photo just shown revert. Delayed a few seconds to give the backend
      // a head start on actually finishing the write, rather than racing it
      // immediately and risking this fetch being the stale one instead.
      setTimeout(async () => {
        try {
          const refreshed: any = await getProfilePicture();
          if (refreshed?.profilePictureUrl) {
            const bustedUrl = withCacheBust(refreshed.profilePictureUrl);
            useUserStore.getState().setProfilePictureUrl(bustedUrl);
          }
        } catch (refreshErr) {
          console.warn("Could not refresh profile picture URL", refreshErr);
        }
      }, 4000);
    } catch (error) {
      console.error("Failed to upload photo", error);
      showToast("Failed to upload photo", "error", 3000);
    } finally {
      hideLoader();
    }
  };

  const handlePhotoModalClose = () => {
    setIsPhotoModalOpen(false);
    setPhotoPreview(null);
  };

  // Load options dynamically using your getAirlines service. Excluded
  // airlines (Flight Search settings) must not appear here — an additional
  // airline can't also be excluded.
  const loadAirlineOptions = async (inputValue: string) => {
    try {
      const res = await getAirlines(inputValue);
      return (
        res
          .filter((airline: AirlineData) => !excludedAirlineIds.has(airline.id))
          .map((airline: AirlineData) => ({
            value: airline.id,
            label: airline.airlineName,
          })) || []
      );
    } catch (err) {
      console.error("Failed to load airlines", err);
      return [];
    }
  };

  // Check if an airline option matches the primary airline
  const isPrimaryAirline = (option: AirlineOption | null): boolean => {
    if (!option || !userProfile?.primaryAirline) return false;
    return option.value === userProfile.primaryAirline.id;
  };

  // When adding additional airlines for the first time
  const handleAddAdditionalAirline = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowAdditionalAirline(true);
    // If we're showing the form for the first time and there were no
    // additional airlines before, mark that we have changes
    if (userProfile?.additionalAirlines?.length === 0) {
      setHasChanges(true);
    }
  };

  // Handlers for airline selection changes
  const handleAirline1Change = (selectedOption: AirlineOption | null) => {
    // Don't allow selection if it matches primary airline
    if (isPrimaryAirline(selectedOption)) {
      return;
    }

    setAirline1(selectedOption);
    // If both additional airlines are the same, clear the second one
    if (airline2 && selectedOption && airline2.value === selectedOption.value) {
      setAirline2(null);
    }
    if (airline3 && selectedOption && airline3.value === selectedOption.value) {
      setAirline3(null);
    }
  };

  const handleAirline2Change = (selectedOption: AirlineOption | null) => {
    // Don't allow selection if it matches primary airline
    if (isPrimaryAirline(selectedOption)) {
      return;
    }

    setAirline2(selectedOption);
    // If both additional airlines are the same, clear the first one
    if (airline1 && selectedOption && airline1.value === selectedOption.value) {
      setAirline1(null);
    }
    if (airline3 && selectedOption && airline3.value === selectedOption.value) {
      setAirline3(null);
    }
  };

  const handleAirline3Change = (selectedOption: AirlineOption | null) => {
    if (isPrimaryAirline(selectedOption)) return;
    setAirline3(selectedOption);

    if (airline1 && selectedOption && airline1.value === selectedOption.value) {
      setAirline1(null);
    }
    if (airline2 && selectedOption && airline2.value === selectedOption.value) {
      setAirline2(null);
    }
  };

  // Save function to prepare data for submission
  const handleSave = () => {
    // Create the payload with the correct structure
    const additionalAirlineIds: number[] = [];

    if (airline1) {
      additionalAirlineIds.push(Number(airline1.value));
    }

    if (airline2) {
      additionalAirlineIds.push(Number(airline2.value));
    }
    if (airline3) {
      additionalAirlineIds.push(Number(airline3.value));
    }

    // An excluded airline can't also be set as an additional airline — catch
    // it here with a clear message instead of letting the save fail with a
    // generic backend error.
    const conflicting = [airline1, airline2, airline3].filter(
      (a): a is AirlineOption => !!a && excludedAirlineIds.has(Number(a.value)),
    );
    if (conflicting.length > 0) {
      const names = conflicting.map((a) => a.label).join(", ");
      showToast(
        `${names} ${conflicting.length > 1 ? "are" : "is"} in your Excluded Airlines list — remove ${
          conflicting.length > 1 ? "them" : "it"
        } from there first.`,
        "error",
        4000,
      );
      return;
    }

    const payload = {
      fullName: userProfile?.fullName || "",
      primaryAirlineId: userProfile?.primaryAirline?.id || 0,
      homebaseId: userProfile?.homebase?.id || 0,
      additionalAirlineIds: additionalAirlineIds,
    };

    // Call the update API
    updateUserProfile(payload)
      .then(() => {
        // Handle success
        // Show success toast
        showToast("Profile updated successfully.", "success", 3000);

        // Update the original state to match the current state
        setOriginalAirline1(airline1);
        setOriginalAirline2(airline2);
        setOriginalAirline3(airline3);
        setHasChanges(false);
      })
      .catch((error) => {
        // Handle error
        console.error("Failed to update profile", error);
        setError("Failed to update profile");
        showToast("Failed to update profile", "error", 3000);
      });
  };

  return (
    <div className="profile">
      <div className="bg-white p-6 rounded-xl">
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
          <i className="st-user text-grease-100 text-lg"></i>
          <p className="text-grease-100 text-base font-semibold">My Account</p>
        </div>

        {/* Profile Avatar Row */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <img
                className="w-14 h-14 rounded-full object-cover"
                src={profilePhoto}
                alt="Profile Avatar"
                onError={(e) => {
                  e.currentTarget.src = AvatarPlaceholder;
                }}
              />
              <div
                className="absolute inset-0 bg-black bg-opacity-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="st-edit text-white text-lg"></i>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoFileSelect}
              className="hidden"
            />
            <div>
              <p className="text-grease-100 text-base font-semibold">{username || userProfile?.fullName || "Username"}</p>
              {/* <p className="text-gray-400 text-xs mt-0.5">Air host</p> */}
            </div>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-gray-400 hover:text-gray-600 p-1"
            title="Change photo"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </button>
        </div>

        {/* General Information */}
        <div>
          <label className="block mb-1.5 text-xs font-medium text-grease-100">Email Address</label>
          <input
            value={String(email || userProfile?.email || "")}
            disabled
            className="block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none disabled:text-gray-500"
          />
        </div>

        {/* Employment Details */}
        <p className="text-grease-100 text-sm font-semibold mt-8 mb-4">Employment Details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block mb-1.5 text-xs font-medium text-grease-100">Airline</label>
            <input
              disabled
              value={userProfile?.primaryAirline?.airlineName || ""}
              className="block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none disabled:text-gray-500"
            />
          </div>
          <div>
            <label className="block mb-1.5 text-xs font-medium text-grease-100">Homebase</label>
            <input
              disabled
              value={userProfile?.homebase?.name || ""}
              className="block w-full p-3.5 rounded-lg bg-white text-sm text-grease-100 border border-gray-200 focus:outline-none disabled:text-gray-500"
            />
          </div>
        </div>

        {!showAdditionalAirline && userProfile?.additionalAirlines?.length === 0 && (
          <a href="#" onClick={handleAddAdditionalAirline} className="flex items-center gap-2 text-sm text-light-yellow font-medium my-6">
            <i className="st-add text-light-yellow text-sm"></i>
            Add Additional Airline
          </a>
        )}

        {/* Additional Airlines */}
        {(showAdditionalAirline || (userProfile?.additionalAirlines && userProfile.additionalAirlines.length > 0)) && (
          <>
            <p className="text-grease-100 text-sm font-semibold mt-8 mb-4">Additional Airlines</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div>
                <label className="block mb-1.5 text-xs font-medium text-grease-100">Primary Additional Airline</label>
                <AsyncSelect<AirlineOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirlineOptions}
                  placeholder="Search and select airline"
                  value={airline1}
                  onChange={handleAirline1Change}
                  isOptionDisabled={(option) =>
                    isPrimaryAirline(option) ||
                    (airline2 !== null && option.value === airline2.value) ||
                    (airline3 !== null && option.value === airline3.value)
                  }
                  styles={customAsyncStyles}
                  menuShouldScrollIntoView={!isIOSDevice}
                  menuPlacement="top"
                />
                {isPrimaryAirline(airline1) && <p className="text-red-500 text-xs mt-1">You cannot select your primary airline again</p>}
              </div>
              <div>
                <label className="block mb-1.5 text-xs font-medium text-grease-100">Secondary Additional Airline</label>
                <AsyncSelect<AirlineOption>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirlineOptions}
                  placeholder="Search and select airline"
                  value={airline2}
                  onChange={handleAirline2Change}
                  isOptionDisabled={(option) =>
                    isPrimaryAirline(option) ||
                    (airline1 !== null && option.value === airline1.value) ||
                    (airline3 !== null && option.value === airline3.value)
                  }
                  styles={customAsyncStyles}
                  menuShouldScrollIntoView={!isIOSDevice}
                  menuPlacement="top"
                />
                {isPrimaryAirline(airline2) && <p className="text-red-500 text-xs mt-1">You cannot select your primary airline again</p>}
              </div>
              <div>
                <label className="block mb-1.5 text-xs font-medium text-grease-100">Tertiary Additional Airline</label>
                <AsyncSelect<AirlineOption>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirlineOptions}
                  placeholder="Select airline"
                  value={airline3}
                  onChange={handleAirline3Change}
                  isOptionDisabled={(option) =>
                    isPrimaryAirline(option) ||
                    (airline1 !== null && option.value === airline1.value) ||
                    (airline2 !== null && option.value === airline2.value)
                  }
                  styles={customAsyncStyles}
                  menuShouldScrollIntoView={!isIOSDevice}
                  menuPlacement="top"
                />
                {isPrimaryAirline(airline3) && <p className="text-red-500 text-xs mt-1">You cannot select your primary airline again</p>}
              </div>
            </div>
          </>
        )}

        {/* Action Buttons — desktop stays a single space-between row (unchanged);
            flex-wrap lets the Discard/Edit group drop to a second line on narrow
            mobile widths instead of overlapping the Delete Account button. */}
        <div className="flex items-center justify-between flex-wrap gap-y-3 mt-8">
          <button
            className="text-red-500 text-sm font-medium border border-red-200 rounded-lg px-5 py-2.5 hover:bg-red-50 transition-colors"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            Delete Account
          </button>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <button
                onClick={() => {
                  setAirline1(originalAirline1);
                  setAirline2(originalAirline2);
                  setAirline3(originalAirline3);
                }}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Discard
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={`inline-flex items-center justify-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-medium leading-none transition-colors ${
                hasChanges
                  ? "bg-[#FFBC00] text-white shadow-button hover:opacity-90"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              Save
            </button>
          </div>
        </div>
      </div>
      {/* Photo Preview Modal */}
      {isPhotoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Preview Photo</h2>
              <button
                onClick={handlePhotoModalClose}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            {photoPreview && (
              <div className="mb-6 flex justify-center">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-48 h-48 rounded-lg object-cover"
                />
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={handlePhotoModalClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePhotoUpload}
                className="flex-1 px-4 py-2 text-white bg-[#FFBC00] rounded-lg hover:bg-[#E6A800] transition-colors"
              >
                Update Photo
              </button>
            </div>
          </div>
        </div>
      )}
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)} // 👈 Close modal
      />
    </div>
  );
};

export default Profile;
